import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { defer } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class HttpRequestQueueService {
  private readonly maxConcurrent = 10;
  private active = 0;
  private readonly waitQueue: (() => void)[] = [];
  private readonly activeCount$ = new BehaviorSubject<number>(0);

  run<T>(taskFactory: () => Observable<T>): Observable<T> {
    return defer(() => this.acquire()).pipe(
      switchMap(() =>
        taskFactory().pipe(
          finalize(() => {
            this.release();
          })
        )
      )
    );
  }

  activeCountChanges(): Observable<number> {
    return this.activeCount$.asObservable();
  }

  isIdle(): boolean {
    return this.active === 0;
  }

  waitForActivity(options?: { timeoutMs?: number; pollMs?: number }): Observable<void> {
    const timeoutMs = options?.timeoutMs ?? 300;
    const pollMs = options?.pollMs ?? 25;

    return new Observable<void>((subscriber) => {
      const startedAt = Date.now();

      const maybeComplete = () => {
        if (this.active > 0) {
          subscriber.next();
          subscriber.complete();
          return;
        }

        if (Date.now() - startedAt >= timeoutMs) {
          subscriber.next();
          subscriber.complete();
        }
      };

      const intervalId = setInterval(maybeComplete, pollMs);
      maybeComplete();

      return () => clearInterval(intervalId);
    });
  }

  waitUntilIdle(options?: {
    idleForMs?: number;
    timeoutMs?: number;
    pollMs?: number;
  }): Observable<void> {
    const idleForMs = options?.idleForMs ?? 300;
    const timeoutMs = options?.timeoutMs ?? 12000;
    const pollMs = options?.pollMs ?? 50;

    return new Observable<void>((subscriber) => {
      const startedAt = Date.now();
      let idleStartedAt: number | null = this.active === 0 ? Date.now() : null;

      const maybeComplete = () => {
        const now = Date.now();
        if (this.active === 0) {
          if (idleStartedAt === null) {
            idleStartedAt = now;
          }
          if (now - idleStartedAt >= idleForMs) {
            subscriber.next();
            subscriber.complete();
            return;
          }
        } else {
          idleStartedAt = null;
        }

        if (now - startedAt >= timeoutMs) {
          subscriber.next();
          subscriber.complete();
        }
      };

      const intervalId = setInterval(maybeComplete, pollMs);
      maybeComplete();

      return () => clearInterval(intervalId);
    });
  }

  private acquire(): Observable<void> {
    if (this.active < this.maxConcurrent) {
      this.active += 1;
      this.activeCount$.next(this.active);
      return of(void 0);
    }

    return new Observable<void>((subscriber) => {
      const resolver = () => {
        this.active += 1;
        this.activeCount$.next(this.active);
        subscriber.next();
        subscriber.complete();
      };

      this.waitQueue.push(resolver);

      return () => {
        const idx = this.waitQueue.indexOf(resolver);
        if (idx > -1) {
          this.waitQueue.splice(idx, 1);
        }
      };
    });
  }

  private release(): void {
    // Always free a slot first.
    // Important: if we immediately hand the slot to a queued request, we must NOT
    // let `active` grow unbounded (which would permanently block future acquires).
    this.active = Math.max(this.active - 1, 0);
    this.activeCount$.next(this.active);

    if (this.waitQueue.length > 0) {
      const next = this.waitQueue.shift();
      if (next) next();
    }
  }
}
